import { computed, type Signal, signal, type WritableSignal } from '@angular/core';
import {
  type CellPrice,
  type FilledCell,
  type GridCoords,
  MAX_PRICE,
  type PriceField,
  priceMargin,
} from './pricing-grid.model';

/**
 * What every water pricing grid store exposes, whatever its cells are keyed by and whichever DTO it
 * loads/saves. `TCoords` is the grid's own coordinate tuple, `TCell` its API cell.
 *
 * Only {@link load} and {@link toPayload} differ per grid — everything else is implemented once in
 * {@link AbstractPriceGridStore}.
 */
export interface PriceGridStore<TCoords extends GridCoords, TCell> {
  /** Set once a cell is edited; components use it to prompt before discarding on a price-list switch. */
  readonly dirty: Signal<boolean>;
  /** Reveal inline validation errors; cleared whenever the grid is bulk-replaced. */
  readonly showErrors: WritableSignal<boolean>;
  /** Keys of cells with exactly one price set (half-filled) — both or neither is required. */
  readonly partialKeys: Signal<string[]>;
  /** Keys of cells holding a price the API would reject outright (negative, or over the DTO ceiling). */
  readonly outOfRangeKeys: Signal<string[]>;

  /** Replace the whole grid from freshly loaded (or empty) cells and mark it pristine. */
  load(cells: TCell[]): void;
  getPurchase(coords: TCoords): number | null;
  getSelling(coords: TCoords): number | null;
  /** Margin % = (selling − purchase) / selling × 100. Null when selling is missing/zero. */
  margin(coords: TCoords): number | null;
  /** True when the cell has exactly one of its two prices set (an invalid, half-filled cell). */
  isCellPartial(coords: TCoords): boolean;
  /** True when the cell is half-filled or holds a price outside the accepted range. */
  isCellInvalid(coords: TCoords): boolean;
  setPrice(coords: TCoords, field: PriceField, value: number | null): void;
  /** True when any cell of the grid holds at least one price. */
  hasFilledCells(): boolean;
  /** Flatten to the save payload: only fully-priced cells. */
  toPayload(): TCell[];
}

/**
 * The in-memory cell map shared by the water pricing grids, plus the `dirty`/`showErrors` flags and all
 * cell math. Every mutation is memory-only — persisting is the owning component's job.
 *
 * Cells live in a flat `Map` keyed by {@link gridKey} over the grid's coordinates, so the number of grid
 * dimensions is the subclass's business only: it maps its coordinates into keys, and shapes
 * {@link filledCells} into its own save DTO. Subclasses read the map through {@link snapshot} for queries
 * that depend on what their coordinates mean (e.g. pruning an axis) and mutate it only through the
 * protected helpers here, which keep the flags consistent.
 */
export abstract class AbstractPriceGridStore<TCoords extends GridCoords, TCell>
  implements PriceGridStore<TCoords, TCell>
{
  private readonly cells = signal<Map<string, CellPrice>>(new Map());

  readonly dirty = signal(false);
  readonly showErrors = signal(false);

  readonly partialKeys = computed(() => this.keysWhere(isPartial));
  readonly outOfRangeKeys = computed(() =>
    this.keysWhere((price) => isOutOfRange(price.purchasePrice) || isOutOfRange(price.sellingPrice)),
  );

  abstract load(cells: TCell[]): void;
  abstract toPayload(): TCell[];

  getPurchase(coords: TCoords): number | null {
    return this.cellAt(coords)?.purchasePrice ?? null;
  }

  getSelling(coords: TCoords): number | null {
    return this.cellAt(coords)?.sellingPrice ?? null;
  }

  margin(coords: TCoords): number | null {
    const cell = this.cellAt(coords);
    return cell ? priceMargin(cell.purchasePrice, cell.sellingPrice) : null;
  }

  isCellPartial(coords: TCoords): boolean {
    const cell = this.cellAt(coords);
    return cell != null && isPartial(cell);
  }

  isCellInvalid(coords: TCoords): boolean {
    const cell = this.cellAt(coords);
    if (!cell) return false;
    return isPartial(cell) || isOutOfRange(cell.purchasePrice) || isOutOfRange(cell.sellingPrice);
  }

  setPrice(coords: TCoords, field: PriceField, value: number | null): void {
    const key = gridKey(coords);
    // Copy-on-write: the app is zoneless, so mutating the existing Map would not re-render.
    const next = new Map(this.cells());
    const existing = next.get(key) ?? { purchasePrice: null, sellingPrice: null };
    next.set(key, { ...existing, [field]: value });
    this.cells.set(next);
    this.dirty.set(true);
  }

  hasFilledCells(): boolean {
    for (const cell of this.cells().values()) {
      if (cell.purchasePrice != null || cell.sellingPrice != null) return true;
    }
    return false;
  }

  /** The current cells, for queries that depend on what the subclass's coordinates mean. */
  protected snapshot(): ReadonlyMap<string, CellPrice> {
    return this.cells();
  }

  protected cellAt(coords: TCoords): CellPrice | undefined {
    return this.cells().get(gridKey(coords));
  }

  /** Replace the whole grid and mark it pristine — used by `load` and by a bulk copy-in. */
  protected replaceCells(entries: Iterable<readonly [string, CellPrice]>, dirty = false): void {
    this.cells.set(new Map(entries));
    this.dirty.set(dirty);
    this.showErrors.set(false);
  }

  /** Drop the given cells (a pruned axis, a cleared sub-grid); leaves the error flag untouched. */
  protected removeCells(keys: Iterable<string>): void {
    const next = new Map(this.cells());
    for (const key of keys) next.delete(key);
    this.cells.set(next);
    this.dirty.set(true);
  }

  /**
   * The fully-priced cells, with their keys decoded back into coordinates. A cell with neither price is
   * omitted, and a half-filled cell cannot reach a save — the component's validation blocks it first.
   */
  protected filledCells(): FilledCell[] {
    const filled: FilledCell[] = [];
    for (const [key, price] of this.cells()) {
      if (price.purchasePrice == null || price.sellingPrice == null) continue;
      filled.push({ coords: parseGridKey(key), purchasePrice: price.purchasePrice, sellingPrice: price.sellingPrice });
    }
    return filled;
  }

  private keysWhere(predicate: (price: CellPrice) => boolean): string[] {
    const keys: string[] = [];
    for (const [key, price] of this.cells()) {
      if (predicate(price)) keys.push(key);
    }
    return keys;
  }
}

/** Build a cell key from its coordinates. The order is the grid's own and must stay stable. */
export function gridKey(coords: GridCoords): string {
  return coords.join(':');
}

/** Split a cell key back into the coordinates {@link gridKey} built it from. */
export function parseGridKey(key: string): number[] {
  return key.split(':').map(Number);
}

/** Round to the two decimals the price columns store. */
export function roundPrice(value: number): number {
  return Math.round(value * 100) / 100;
}

function isPartial(price: CellPrice): boolean {
  return (price.purchasePrice == null) !== (price.sellingPrice == null);
}

function isOutOfRange(value: number | null): boolean {
  return value != null && (value < 0 || value > MAX_PRICE);
}
